import { PocSwitch } from 'commons/components/components-wrapper/poc-switch.component';
import { PocTooltip } from 'commons/components/components-wrapper/poc-tooltip.component';
import { Input, TooltipedIconComponent } from 'commons/components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'core/i18n/i18n';
import {
    TrainingRegistration,
    TrainingRegistrationCustomErrors,
    TrainingRegistrationValidation,
    defaultTrainingRegistrationCustomErrors,
    defaultTrainingRegistrationValidition,
} from '../types/lms-training-registration.type';
import { TrainingRegistrationLayout, TrainingRegistrationOptions } from './training-registration-management.style';
import { getTrainingRegistrationValidationMessages } from './training-registration-management.validation';
import { CustomValidationCode } from 'core/utils/backend-service';

interface TrainingRegistrationManagementComponentProps {
    trainingRegistration: TrainingRegistration;
    trainingRegistrations: TrainingRegistration[];
    onChange: (updatedTraining: TrainingRegistration) => void;
    onRemove: (removedTraining: TrainingRegistration) => void;
}

export const TrainingRegistrationManagementComponent: React.FC<TrainingRegistrationManagementComponentProps> = (
    props
) => {
    const { trainingRegistration, trainingRegistrations, onChange, onRemove } = props;

    const [trainingRegistrationValidation, setTrainingRegistrationValidation] =
        useState<TrainingRegistrationValidation>(defaultTrainingRegistrationValidition);
    const [trainingRegistrationCustomErrors, setTrainingRegistrationCustomErrors] =
        useState<TrainingRegistrationCustomErrors>(defaultTrainingRegistrationCustomErrors);

    const { t } = useTranslation();

    /* workaround */
    useEffect(() => {
        handleValidity('externalId');
    }, [trainingRegistration.externalId]);

    useEffect(() => {
        handleValidity('name');
    }, [trainingRegistration.name]);

    const handleValidity = (field: keyof TrainingRegistration) => {
        const uniqueList = trainingRegistrations.map((tr) => tr[field]);
        if (trainingRegistration[field] !== undefined && !trainingRegistration[field]) {
            setTrainingRegistrationValidation({
                ...trainingRegistrationValidation,
                [field]: {
                    state: 'error',
                    type: ['value-missing'],
                },
            });
        } else if (
            trainingRegistration[field] !== undefined &&
            uniqueList.indexOf(trainingRegistration[field]) !== uniqueList.lastIndexOf(trainingRegistration[field])
        ) {
            setTrainingRegistrationValidation({
                ...trainingRegistrationValidation,
                [field]: {
                    state: 'error',
                    type: ['custom'],
                },
            });
            setTrainingRegistrationCustomErrors({
                ...trainingRegistrationCustomErrors,
                [field]: ['duplicated'],
            });
        } else {
            setTrainingRegistrationValidation({
                ...trainingRegistrationValidation,
                [field]: {
                    state: 'valid',
                    type: [],
                },
            });
            setTrainingRegistrationCustomErrors({
                ...trainingRegistrationCustomErrors,
                [field]: [],
            });
        }
    };
    /* end workaround */

    return (
        <TrainingRegistrationLayout>
            <Input
                spaceForError
                disabled={!!trainingRegistration.id}
                className="without-label"
                name={`trainingRegistration-${trainingRegistration.index}-name`}
                placeholder="settings.integrations.lms-api-integration.training-registration.name.placeholder"
                value={trainingRegistration.name}
                onChange={(_field, value) =>
                    onChange({
                        ...trainingRegistration,
                        name: value ?? '',
                    })
                }
                required
                maxlength={255}
                validationMessages={getTrainingRegistrationValidationMessages(t)['trainingRegistration-name']}
                validity={trainingRegistrationValidation.name}
                customValidationCode={trainingRegistrationCustomErrors.name as unknown as CustomValidationCode}
                /* workaround */
                onCustomBlur={() => {
                    if (trainingRegistration.name === undefined) {
                        onChange({
                            ...trainingRegistration,
                            name: '',
                        });
                    }
                }}
                /*onValidity={(_field, value) =>
                    setTrainingRegistrationValidation({ ...trainingRegistrationValidation, name: value })
                }*/
            />
            {trainingRegistration.id != null && (
                <PocTooltip anchor={`trainingRegistration-${trainingRegistration.index}-name`}>
                    {t('settings.integrations.lms-api-integration.training-registration.non-editable-field')}
                </PocTooltip>
            )}
            <Input
                spaceForError
                disabled={!!trainingRegistration.id}
                className="without-label"
                name={`trainingRegistration-${trainingRegistration.index}-externalId`}
                placeholder="settings.integrations.lms-api-integration.training-registration.external-id.placeholder"
                value={trainingRegistration.externalId}
                onChange={(_field, value) =>
                    onChange({
                        ...trainingRegistration,
                        externalId: value ?? '',
                    })
                }
                required
                maxlength={255}
                validationMessages={getTrainingRegistrationValidationMessages(t)['trainingRegistration-externalId']}
                validity={trainingRegistrationValidation.externalId}
                customValidationCode={trainingRegistrationCustomErrors.externalId as unknown as CustomValidationCode}
                /* workaround */
                onCustomBlur={() => {
                    if (trainingRegistration.externalId === undefined) {
                        onChange({
                            ...trainingRegistration,
                            externalId: '',
                        });
                    }
                }}
            />
            {trainingRegistration.id != null && (
                <PocTooltip anchor={`trainingRegistration-${trainingRegistration.index}-externalId`}>
                    {t('settings.integrations.lms-api-integration.training-registration.non-editable-field')}
                </PocTooltip>
            )}
            <PocSwitch
                id={`trainingRegistration-${trainingRegistration.index}-active`}
                checked={trainingRegistration.active}
                onValueChange={(ev) =>
                    onChange({
                        ...trainingRegistration,
                        active: ev.detail,
                    })
                }
            />
            <TrainingRegistrationOptions>
                <PocTooltip anchor={`trainingRegistration-${trainingRegistration.index}-active`}>
                    {t(
                        `settings.integrations.lms-api-integration.training-registration.actions.${
                            trainingRegistration.active ? 'deactivate' : 'activate'
                        }`
                    )}
                </PocTooltip>
                {!trainingRegistration.id && (
                    <TooltipedIconComponent
                        icon="delete"
                        tooltipText={t(
                            'settings.integrations.lms-api-integration.training-registration.actions.delete'
                        )}
                        family="filled"
                        onClick={() => onRemove(trainingRegistration)}
                        dataTestId="training-registration-delete"
                    />
                )}
            </TrainingRegistrationOptions>
        </TrainingRegistrationLayout>
    );
};
